import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProfiles } from '../../slices/ads/thunk';
import { 
    updateProfileCache, 
    clearProfileCache, 
    updateDashboardAdsCache,
    updateProfileInCache,
    removeProfileFromCache
} from '../../slices/ads/reducer';
import { updateAdsCache } from '../../slices/dashboard/reducer';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useProfileCache = () => {
    const dispatch = useDispatch();
    const profileCache = useSelector((state) => state.Profiles?.profileCache);
    const dashboardAdsCache = useSelector((state) => state.Dashboard?.adsCache);
    const loading = useSelector((state) => state.Profiles?.loading);

    // Check if cache is valid
    const isCacheValid = useCallback((lastFetched) => {
        if (!lastFetched) return false;
        return Date.now() - lastFetched < CACHE_DURATION;
    }, []);

    // Get cached profiles with validation
    const getCachedProfiles = useCallback((filterState = null, sort = null) => {
        if (!profileCache?.data?.length) return null;
        
        // Check if filters/sort match using filterState (the raw filter state from component)
        const filtersMatch = !filterState || 
            (profileCache.filterState?.filterOption === filterState.filterOption && 
             profileCache.filterState?.filterValue === filterState.filterValue);
        
        const sortMatch = !sort || profileCache.sort?.value === sort.value;
        
        if (filtersMatch && sortMatch && isCacheValid(profileCache.lastFetched)) {
            return {
                data: profileCache.data,
                total: profileCache.total,
                hasMore: profileCache.hasMore,
                currentPage: profileCache.currentPage
            };
        }
        
        return null;
    }, [profileCache, isCacheValid]);

    // Get cached dashboard ads
    const getCachedDashboardAds = useCallback(() => {
        if (!dashboardAdsCache?.data?.length) return null;
        
        if (isCacheValid(dashboardAdsCache.lastFetched)) {
            return dashboardAdsCache.data;
        }
        
        return null;
    }, [dashboardAdsCache, isCacheValid]);

    // Fetch profiles with caching logic
    const fetchProfiles = useCallback(async ({ 
        page = 1, 
        limit = 6, 
        status = null, 
        filters = null, 
        sort = null, 
        filterState = null,
        forceRefresh = false 
    } = {}) => {
        // Check cache first (unless force refresh)
        if (!forceRefresh) {
            const cached = getCachedProfiles(filterState, sort);
            if (cached && page <= cached.currentPage) {
                return cached;
            }
        }

        try {
            // Fetch from API
            const result = await dispatch(getAllProfiles({ 
                page, 
                limit, 
                status, 
                filters, 
                sort, 
                filterState,
                forceRefresh 
            }));

            if (result.type.endsWith('/fulfilled')) {
                // Ensure the payload has the expected structure
                const payload = result.payload || {};
                return {
                    data: Array.isArray(payload.data) ? payload.data : [],
                    total: payload.total || 0,
                    page: payload.page || page,
                    hasMore: payload.hasMore || false,
                    filters: payload.filters || filters,
                    filterState: payload.filterState || filterState,
                    sort: payload.sort || sort
                };
            }
            
            throw new Error(result.payload?.error || 'Failed to fetch profiles');
        } catch (error) {
            console.error('Error fetching profiles:', error);
            throw error;
        }
    }, [dispatch, getCachedProfiles]);

    // Load more profiles (pagination)
    const loadMoreProfiles = useCallback(async (filters = null, sort = null) => {
        const currentPage = profileCache?.currentPage || 1;
        const nextPage = currentPage + 1;
        
        if (profileCache?.hasMore === false) {
            return { data: [], hasMore: false };
        }

        return await fetchProfiles({ 
            page: nextPage, 
            filters, 
            sort 
        });
    }, [profileCache, fetchProfiles]);

    // Clear cache
    const clearCache = useCallback(() => {
        dispatch(clearProfileCache());
    }, [dispatch]);

    // Update profile in cache
    const updateProfile = useCallback((adId, updatedData) => {
        dispatch(updateProfileInCache({ adId, updatedData }));
    }, [dispatch]);

    // Remove profile from cache
    const removeProfile = useCallback((adId) => {
        dispatch(removeProfileFromCache(adId));
    }, [dispatch]);

    // Memoized cache status
    const cacheStatus = useMemo(() => ({
        hasData: !!profileCache?.data?.length,
        total: profileCache?.total || 0,
        totalPages: profileCache?.totalPages || 0,
        currentPage: profileCache?.currentPage || 1,
        hasMore: profileCache?.hasMore || false,
        lastFetched: profileCache?.lastFetched,
        isStale: profileCache?.lastFetched ? !isCacheValid(profileCache.lastFetched) : true
    }), [profileCache, isCacheValid]);

    // Get profiles from either cache or direct profiles array as fallback
    const directProfiles = useSelector((state) => state.Profiles?.profiles);
    const profilesData = profileCache?.data?.length > 0 ? profileCache.data : (directProfiles || []);

    // Debug logging
    useEffect(() => {
        console.log('useProfileCache Debug - profileCache:', profileCache);
        console.log('useProfileCache Debug - directProfiles:', directProfiles);
        console.log('useProfileCache Debug - profilesData:', profilesData);
        console.log('useProfileCache Debug - loading:', loading);
    }, [profileCache, directProfiles, profilesData, loading]);

    return {
        // Data
        profiles: profilesData,
        dashboardAds: dashboardAdsCache?.data || [],
        loading,
        
        // Cache status
        cacheStatus,
        
        // Actions
        fetchProfiles,
        loadMoreProfiles,
        clearCache,
        updateProfile,
        removeProfile,
        
        // Cache validation
        isCacheValid: (lastFetched) => isCacheValid(lastFetched),
        getCachedProfiles,
        getCachedDashboardAds
    };
};

export default useProfileCache;
